import React, { useRef, useState } from "react";
import { TransitionProps } from "@mui/material/transitions";

import Slider from "react-slick";
import Dialog from "@mui/material/Dialog";
import Slide from "@mui/material/Slide";
import InstallMobileIcon from "@mui/icons-material/InstallMobile";

//* Hooks
import { IBeforeInstallPromptEvent } from "../../hooks/UseAddToHomeScreen";

//* Contexts
import { useTranslationsContext } from "../../contexts/TranslationsContext";

//* Components
import StepOne from "./step-one/StepOne";
import StepTwo from "./step-two/StepTwo";
import StepThree from "./step-three/StepThree";
import StepFour from "./step-four/StepFour";

//* Assets
import homeScreenApp from "../../assets/ilustrations/homeScreen.svg";

//* Styles
import "./AddToHomeScreen.css";

type AddToHomeScreenDialogProps = {
  open: boolean;
  onClose(): void;
  onHandleSubmit(): void;
  installed: boolean;
  prompt: IBeforeInstallPromptEvent | null;
  promptToInstall(): void;
};

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

function AddHomeScreenSlider({
  open,
  onClose,
  onHandleSubmit,
  prompt = null,
  installed = false,
  promptToInstall,
}: AddToHomeScreenDialogProps): JSX.Element {
  const sliderRef = useRef<Slider>(null);
  const [currentTabIndex, setCurrentTabIndex] = useState<number>(0);
  const { translation } = useTranslationsContext();

  const isNativePrompt = prompt && !installed;
  const totalSteps = 5;

  function handleCloseModal() {
    onClose();
  }

  function handleVerification() {
    onHandleSubmit();
  }

  const next = () => {
    sliderRef?.current?.slickNext();
  };
  const previous = () => {
    sliderRef?.current?.slickPrev();
  };

  const settings = {
    dots: false,
    infinite: false,
    speed: 400,
    slidesToShow: 1,
    slidesToScroll: 1,
    initialSlide: 0,
    swipeToSlide: true,
    arrows: false,
    afterChange: (current: number) => {
      setCurrentTabIndex(current);
    },
  };

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={handleCloseModal}
      aria-labelledby="add-to-home-screen-dialog-title"
      className="ath-dialog"
      TransitionComponent={Transition}
    >
      {/* ── Top bar ── */}
      <div className="ath-topbar">
        <button className="ath-close-btn" onClick={handleCloseModal} aria-label="close">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M15 5L5 15M5 5l10 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
      </div>

      {isNativePrompt ? (
        /* ── Native install prompt ── */
        <div className="ath-native">
          {/* TutoriFy logo */}
          <div className="ath-logo">
            <svg width="64" height="64" viewBox="0 0 50 50" fill="none">
              <rect width="50" height="50" rx="12" fill="#7aacb3" />
              <path d="M25 10L7 19l18 9 18-9-18-9z" fill="white" opacity="0.95" />
              <path d="M13 24v9c0 3.3 5.4 6 12 6s12-2.7 12-6v-9L25 30 13 24z" fill="white" opacity="0.7" />
              <rect x="37" y="19" width="2.5" height="10" rx="1.25" fill="white" opacity="0.9" />
              <circle cx="38.25" cy="31" r="2" fill="white" opacity="0.9" />
            </svg>
          </div>

          <h1 className="ath-brand">TutoriFy</h1>
          <p className="ath-native-text">
            {translation.INSTALL_APP_STEP_0.defaultMessage}
          </p>

          <img
            className="ath-hero-img"
            src={homeScreenApp}
            alt="Install app"
          />

          <div className="ath-native-actions">
            <button className="ath-btn-primary" onClick={promptToInstall}>
              <InstallMobileIcon sx={{ fontSize: 20 }} />
              {translation.APP_INSTALL.defaultMessage}
            </button>
            <button className="ath-btn-ghost" onClick={handleCloseModal}>
              {translation.LATER.defaultMessage}
            </button>
          </div>
        </div>
      ) : (
        /* ── Manual step-by-step carousel ── */
        <div className="ath-carousel">
          {/* Progress bar */}
          <div className="ath-progress">
            {Array.from({ length: totalSteps }).map((_, i) => (
              <div
                key={i}
                className={`ath-progress-dot ${i === currentTabIndex ? "ath-progress-dot--active" : ""} ${i < currentTabIndex ? "ath-progress-dot--done" : ""}`}
              />
            ))}
          </div>

          {/* Step label */}
          <div className="ath-step-label">
            {currentTabIndex === 0 ? (
              <span className="ath-brand-inline">TutoriFy</span>
            ) : (
              <span className="ath-step-count">
                {currentTabIndex} <span className="ath-step-of">/ {totalSteps - 1}</span>
              </span>
            )}
          </div>

          {/* Slider */}
          <div className="ath-slider-wrap">
            <Slider ref={sliderRef} {...settings}>
              {/* Slide 0: Intro */}
              <div className="ath-slide">
                <div className="ath-intro">
                  <div className="ath-logo">
                    <svg width="56" height="56" viewBox="0 0 50 50" fill="none">
                      <rect width="50" height="50" rx="12" fill="#7aacb3" />
                      <path d="M25 10L7 19l18 9 18-9-18-9z" fill="white" opacity="0.95" />
                      <path d="M13 24v9c0 3.3 5.4 6 12 6s12-2.7 12-6v-9L25 30 13 24z" fill="white" opacity="0.7" />
                      <rect x="37" y="19" width="2.5" height="10" rx="1.25" fill="white" opacity="0.9" />
                      <circle cx="38.25" cy="31" r="2" fill="white" opacity="0.9" />
                    </svg>
                  </div>
                  <p className="ath-intro-text">
                    {translation.INSTALL_APP_STEP_0.defaultMessage}
                  </p>
                  <img
                    className="ath-hero-img"
                    src={homeScreenApp}
                    alt="Install app"
                  />
                </div>
              </div>

              {/* Slide 1-4: Steps */}
              <div className="ath-slide">
                <StepOne />
              </div>
              <div className="ath-slide">
                <StepTwo />
              </div>
              <div className="ath-slide">
                <StepThree />
              </div>
              <div className="ath-slide">
                <StepFour />
              </div>
            </Slider>
          </div>

          {/* Navigation */}
          <div className="ath-nav">
            {currentTabIndex > 0 && (
              <button className="ath-btn-ghost" onClick={previous}>
                {translation.BACK.defaultMessage}
              </button>
            )}
            <div className="ath-nav-spacer" />
            {currentTabIndex < totalSteps - 1 && (
              <button className="ath-btn-primary" onClick={next}>
                {translation.CONTINUE.defaultMessage}
              </button>
            )}
            {currentTabIndex === totalSteps - 1 && (
              <button className="ath-btn-primary" onClick={handleVerification}>
                {translation.CONFIRM.defaultMessage}
              </button>
            )}
          </div>
        </div>
      )}
    </Dialog>
  );
}

export default AddHomeScreenSlider;
